import PropTypes from 'prop-types';

import { useClickstreamCallback } from '@zenbusiness/application-commons-clickstream';
import { useIntl } from '@zenbusiness/application-commons-intl';
import Chip from '@zenbusiness/zui/Chip';
import Link from '@zenbusiness/zui/Link';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';

import { ProductBenefits } from './ProductBenefits';

export function SubscriptionDetails({
  serviceName,
  summary,
  benefits,
  included,
  inTrial,
  activationPath,
  activationLinkText,
  partnerDomain,
}) {
  const { formatMessage } = useIntl();
  const trackActivationLink = useClickstreamCallback('linkPressed', {
    elementName: 'subscription_activation_link',
    elementText: activationLinkText,
  });

  const formattedLinkText = activationLinkText ? formatMessage({ id: activationLinkText }) : null;

  return (
    <Stack gap={0.5}>
      <Stack direction="row" alignItems="center" gap={1}>
        <Text size="lg" weight="bold">
          {serviceName}
        </Text>
        {inTrial && (
          <Chip
            label={formatMessage({ id: 'dashboard.subscriptions.details.freeTrial' })}
            size="medium"
            color="success"
          />
        )}
      </Stack>
      {summary && (
        <Text size="sm" color="secondary">
          {summary}
        </Text>
      )}
      {partnerDomain?.domainName && (
        <Stack direction="row">
          <Chip
            label={partnerDomain.domainName}
            size="sm"
            sx={(theme) => ({
              backgroundColor: theme.palette.background.blue.light,
            })}
          />
        </Stack>
      )}
      {!included && <ProductBenefits benefits={benefits} />}
      {activationPath && formattedLinkText && (
        <Link
          href={activationPath}
          underline="none"
          onClick={trackActivationLink}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.25rem',
            fontSize: '0.875rem',
            fontWeight: 'bold',
          }}
        >
          {formattedLinkText}
          {' →'}
        </Link>
      )}
    </Stack>
  );
}

SubscriptionDetails.propTypes = {
  serviceName: PropTypes.string.isRequired,
  summary: PropTypes.string,
  benefits: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      description: PropTypes.string,
    }),
  ),
  included: PropTypes.bool,
  inTrial: PropTypes.bool,
  activationPath: PropTypes.string,
  activationLinkText: PropTypes.string,
  partnerDomain: PropTypes.shape({
    domainName: PropTypes.string,
  }),
};
